// Template ⇄ YAML round-trip.
//
// A Margin Pro template is a formatted HTML <table> (header row + cells).
// Export flattens it to readable YAML (columns + rows). A cell with no
// formatting becomes a clean string; a cell with bold/bullets/etc keeps its
// inline HTML so the import renders identically ("hybrid" fidelity).
//
// Import is a trust boundary: the file is user-supplied, so every rebuilt cell
// is DOMPurify-sanitized with the same allowlist TemplateEditor uses.

import yaml from "js-yaml";
import DOMPurify from "dompurify";

export interface TemplateYaml {
  name: string;
  columns: string[];
  rows: string[][];
}

// Mirrors TemplateEditor's HEADER_STYLE / CELL_STYLE so imports look native.
const HEADER_STYLE =
  "background-color:#f3f2f1;padding:10px 14px;border:0.5pt solid #d1d1d1;text-align:left;font-weight:700;font-family:Calibri,sans-serif;font-size:10pt;";
const CELL_STYLE =
  "padding:10px 14px;border:0.5pt solid #d1d1d1;vertical-align:top;color:#444;font-family:Calibri,sans-serif;font-size:10pt;min-width:80px;";

const ALLOWED_TAGS = [
  "table", "thead", "tbody", "tr", "th", "td",
  "p", "strong", "em", "b", "i", "u", "br",
  "ul", "ol", "li", "span", "div",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "colgroup", "col",
];
const ALLOWED_ATTR = ["style", "class", "colspan", "rowspan", "contenteditable"];

// A cell keeps its HTML only if it actually carries formatting; otherwise we
// store the plain text so simple templates diff cleanly in git.
// ponytail: regex sniff, not a full parse — upgrade if cells ever nest tables.
const FORMATTING_RE = /<(strong|em|b|i|u|ul|ol|li|h[1-6]|br|span)[\s>/]/i;

function cellToValue(cell: Element): string {
  const html = cell.innerHTML.trim();
  if (FORMATTING_RE.test(html)) return html;
  return (cell.textContent || "").trim();
}

/** Formatted-table HTML → YAML string. Throws if no table is found. */
export function templateToYaml(name: string, html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const table = doc.querySelector("table");
  if (!table) throw new Error("This template has no table to export.");

  const trs = Array.from(table.querySelectorAll("tr"));
  if (trs.length === 0) throw new Error("This template's table is empty.");

  const columns = Array.from(trs[0].querySelectorAll("th, td")).map(cellToValue);
  const rows = trs.slice(1).map((tr) =>
    Array.from(tr.querySelectorAll("th, td")).map(cellToValue)
  );

  const payload: TemplateYaml = { name: (name || "Untitled Template").trim(), columns, rows };
  // lineWidth:-1 keeps long cell values on one line (cleaner git diffs).
  return yaml.dump(payload, { lineWidth: -1, noRefs: true });
}

/** YAML string → { name, html }. Sanitizes all rebuilt HTML. Throws on bad input. */
export function yamlToTemplate(yamlText: string): { name: string; html: string } {
  let parsed: unknown;
  try {
    // JSON_SCHEMA permits only plain JSON types (no custom/JS tags) — no code
    // execution path. (js-yaml 4's default load is already safe; this is explicit.)
    parsed = yaml.load(yamlText, { schema: yaml.JSON_SCHEMA });
  } catch (e: any) {
    throw new Error(`That file isn't valid YAML: ${e?.reason || e?.message || "parse error"}`);
  }
  const t = parsed as Partial<TemplateYaml>;
  if (!t || !Array.isArray(t.columns) || t.columns.length === 0) {
    throw new Error("This file doesn't look like a Margin Pro template (missing 'columns').");
  }
  const rows: string[][] = Array.isArray(t.rows) ? t.rows : [];

  const esc = (s: unknown) => String(s ?? "");
  const headerCells = t.columns
    .map((c) => `<th style="${HEADER_STYLE}" contenteditable="true">${esc(c)}</th>`)
    .join("");
  // Normalize ragged rows to the column count so the table stays rectangular.
  const bodyRows = (rows.length ? rows : [t.columns.map(() => "")])
    .map((r) => {
      const cells = Array.from({ length: t.columns!.length }, (_, i) =>
        `<td style="${CELL_STYLE}" contenteditable="true">${esc(r[i])}</td>`
      ).join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const rawHtml = `<table><tr>${headerCells}</tr>${bodyRows}</table>`;
  const clean = DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR });
  // Name is plain text only — strip any tags a crafted file might include.
  const name = DOMPurify.sanitize(String(t.name || "Imported Template"), { ALLOWED_TAGS: [] }).trim();

  return { name: name || "Imported Template", html: clean };
}
